/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { Grid, IconButton, Link, Typography2 } from '@coursera/cds-core';
import { ExternalLinkIcon } from '@coursera/cds-icons';

import ExternalLinksWrapper from 'bundles/assess-common/components/ExternalLinksWrapper';
import UploadedFilePreview from 'bundles/assess-common/components/UploadedFilePreview';
import { getFileSizeBytesFromUrl } from 'bundles/assess-common/utils/fileUploadUtils';
import { TooltipWrapper } from 'bundles/common/components/Tooltip';

import _t from 'i18n!nls/assess-common';

type Props = {
  fileUrl: string;
  disabled: boolean;
  filename?: string;
  onRemoveUploadedFile?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
};

const styles = {
  container: () => css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: var(--cds-border-radius-100);
    width: 100%;
    height: 100px;
    padding: 0 var(--cds-spacing-200);
    overflow: hidden;
  `,
  filenameContainer: () => css`
    padding: 0 var(--cds-spacing-200);
  `,
  filename: () => css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: 900px) {
      max-width: 300px;
    }
  `,
  link: () => css`
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-shrink: 0;
    min-width: 0;
    color: var(--cds-color-neutral-primary);
    width: var(--cds-spacing-400);
    height: var(--cds-spacing-400);

    &:visited {
      color: var(--cds-color-neutral-primary) !important;
    }

    &:hover {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      color: var(--cds-color-interactive-primary-hover) !important;
    }
  `,
  delete: () => css`
    margin-left: var(--cds-spacing-50);
    flex-shrink: 0;
    color: var(--cds-color-neutral-primary) !important;
    width: var(--cds-spacing-400) !important;
    height: var(--cds-spacing-400) !important;

    &:hover {
      color: var(--cds-color-interactive-primary-hover);
    }
  `,
};

const UploadedFileBlock = ({ fileUrl, filename, disabled, onRemoveUploadedFile }: Props) => {
  const [fileSizeBytes, setFileSizeBytes] = useState<number | null>(null);

  useEffect(() => {
    const fetchFileSizeBytes = async () => {
      if (fileUrl) {
        const fileSize = await getFileSizeBytesFromUrl(fileUrl);
        setFileSizeBytes(fileSize);
      }
    };
    fetchFileSizeBytes();
  }, [fileUrl]);

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      css={styles.container}
      data-testid="uploaded-file-block"
    >
      <Grid item xs={2}>
        <Grid item container justifyContent="center">
          <UploadedFilePreview fileUrl={fileUrl} />
        </Grid>
      </Grid>
      <Grid item xs={8} css={styles.filenameContainer}>
        {filename && (
          <Typography2
            variant="actionPrimary"
            component="div"
            data-testid="uploaded-file-block-filename"
            // can be either file name or title, so keeping prefix generic
            title={_t('File: #{filename}', { filename })}
            css={styles.filename}
          >
            {filename}
          </Typography2>
        )}
        {fileSizeBytes && (
          <Typography2
            variant="bodySecondary"
            component="div"
            data-testid="uploaded-file-block-filesize"
            color="supportText"
          >
            {fileSizeBytes < 1_000_000
              ? `${Math.ceil(fileSizeBytes / 1_000)}KB`
              : `${Math.ceil(fileSizeBytes / 1_000_000)}MB`}
          </Typography2>
        )}
      </Grid>
      <Grid item xs={2}>
        <Grid item container justifyContent="flex-end">
          <ExternalLinksWrapper>
            <TooltipWrapper placement="bottom" message={_t('Open in new tab')}>
              <Link
                rel="noopener noreferrer"
                target="_blank"
                href={fileUrl}
                aria-label={_t('Open uploaded file #{filename}', { filename })}
                css={styles.link}
                data-testid="uploaded-file-block-link"
              >
                <ExternalLinkIcon />
              </Link>
            </TooltipWrapper>
          </ExternalLinksWrapper>
          {!disabled && (
            <TooltipWrapper placement="bottom" message={_t('Remove file')}>
              <IconButton
                variant="ghost"
                intent="delete"
                onClick={onRemoveUploadedFile}
                onKeyUp={onRemoveUploadedFile}
                tabIndex={0}
                aria-label={_t('Remove uploaded file')}
                css={styles.delete}
                // the styling of the icon button tooltip is different than tooltip wrapper
                // using tooltip wrapper too keep consistency
                hideTooltip
              />
            </TooltipWrapper>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default UploadedFileBlock;
